import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { ShieldPanelCustomization } from './components/my-team/ShieldPanelCustomization';
import { parseParamsTshirtAndShield, shieldKeys } from './functions/utils';

import { Button, Loader, Tabs } from '@/components';
import { ShieldTeam } from '@/components/ShieldTeam';
import { TabType } from '@/components/Types';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import useTshirtAndShield from '@/hooks/useTshirtAndShield';
import ROUTES from '@/router/routes';
import { saveShield } from '@/services/editAccount';
import {
  defaultShieldTeam,
  initialTshitAndShield,
} from '@/store/tshirtAndShield';
import { Svg, SvgEscudo } from '@/types/editProfile';

export const ShieldPanel = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isMobile, isDesktop } = useIsDesktop();

  const { data: dataShieldTshirt, updateTshirtAndShield } =
    useTshirtAndShield();

  const [customizationValues, setCustomizationValues] = useState<
    Svg | undefined
  >({ escudo: dataShieldTshirt?.shield });

  const handleCustomizationValuesChange = (customizationValues: Svg) => {
    setCustomizationValues(customizationValues);
  };

  const normalize = (v: string | null | undefined) => {
    // convierte null/undefined a '', elimina espacios y trata los strings "null"/"undefined" como vacíos
    const raw = v ?? '';
    if (raw === 'null' || raw === 'undefined') return '';
    return String(raw).trim();
  };

  const disabledSubmit = useMemo(() => {
    const shield = dataShieldTshirt?.shield ?? {};
    const customValue = customizationValues?.escudo ?? {};

    return (
      normalize(shield.escudoparam1) === normalize(customValue.escudoparam1) &&
      normalize(shield.escudoparam2) === normalize(customValue.escudoparam2) &&
      normalize(shield.escudoparam3) === normalize(customValue.escudoparam3) &&
      normalize(shield.escudoparam4) === normalize(customValue.escudoparam4) &&
      normalize(shield.escudoparam5) === normalize(customValue.escudoparam5)
    );
  }, [customizationValues?.escudo, dataShieldTshirt?.shield]);

  const handleSaveChanges = async () => {
    if (!customizationValues?.escudo?.escudoparam5) {
      toast.show('Debés ingresar siglas para tu quipo en el escudo.');
      return;
    }

    const param1 =
      !customizationValues.escudo.escudoparam1 &&
      !defaultShieldTeam.escudoparam1
        ? '1'
        : customizationValues.escudo.escudoparam1 ??
          defaultShieldTeam.escudoparam1;

    try {
      const response = await saveShield({
        param1,
        param2:
          customizationValues.escudo.escudoparam2 ??
          defaultShieldTeam.escudoparam2,
        param3:
          customizationValues.escudo.escudoparam3 ??
          defaultShieldTeam.escudoparam3,
        param4:
          customizationValues.escudo.escudoparam4 ??
          defaultShieldTeam.escudoparam4,
        param5:
          customizationValues.escudo.escudoparam5 ??
          defaultShieldTeam.escudoparam5,
      });
      if (response) {
        if (response.estado) {
          toast.show('Se grabaron los cambios del escudo');
          queryClient.invalidateQueries('initialDataTshitAndShield');
          // Generamos los datos dinámicamente para actualizar el estado global
          const shieldData = parseParamsTshirtAndShield<SvgEscudo>(
            response?.escudo_svg,
            shieldKeys,
            initialTshitAndShield.shield
          );

          updateTshirtAndShield({
            shield: shieldData,
          });
        }
        toast.show(response.mensaje);
      }
    } catch (error) {
      toast.show('Ocurrió un error al guardar los cambios');
    }
  };

  const handleGoBack = () => {
    navigate(ROUTES.editAccount.myTeam);
    queryClient.invalidateQueries('initialDataTshitAndShield');
  };

  const ActionButtonSaveTshirt = () => {
    return (
      <Button
        className="lg:mt-20 lg:w-60 w-full"
        disabled={disabledSubmit}
        type="button"
        variant={'primary'}
        onClick={handleSaveChanges}
      >
        Guardar cambios
      </Button>
    );
  };

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (loading) setLoading(() => false);
  }, [loading]);

  return (
    <>
      <Loader isVisible={loading} />
      <div className="lg:mt-7 flex flex-wrap pb-28 lg:pb-0">
        {isMobile && (
          <Tabs
            activeTab={TabType.SecondTab}
            className="w-full px-4 mt-4"
            tabs={[{ name: 'Mi camiseta' }, { name: 'Escudo' }]}
            onClick={(tab) =>
              tab === TabType.FirstTab &&
              navigate(ROUTES.editAccount.tshirtPanel)
            }
          />
        )}
        <div className="lg:pl-[50px] text-gdt-blue text-lg font-semibold cursor-pointer hidden lg:flex gap-2">
          <p>{'<'}</p>
          <Button
            className="text-[16px] font-bold inline-block no-underline"
            type="button"
            variant="text"
            onClick={handleGoBack}
          >
            Volver
          </Button>
        </div>

        <div className="flex justify-center flex-col lg:flex-row">
          <div className="w-screen lg:max-w-[460px] text-center">
            <figure className="mt-6 lg:mt-10 mx-auto">
              <ShieldTeam
                backgroundColor={`#${customizationValues?.escudo?.escudoparam4}`}
                className="w-[201px] h-[227px] lg:w-[240px] lg:h-[271px] m-auto"
                id={customizationValues?.escudo?.escudoparam1 || '1'}
                pattern={customizationValues?.escudo?.escudoparam2}
                patternColor={`#${customizationValues?.escudo?.escudoparam3}`}
                textContent={customizationValues?.escudo?.escudoparam5}
              />
            </figure>
            {isDesktop && <ActionButtonSaveTshirt />}
          </div>
          <ShieldPanelCustomization
            customizationValues={customizationValues}
            handleCustomizationValuesChange={handleCustomizationValuesChange}
          />
        </div>
        {isMobile && (
          <div className="fixed bottom-0 left-0 z-10 w-full bg-white flex justify-center border-t shadow-sm p-4">
            <ActionButtonSaveTshirt />
          </div>
        )}
      </div>
    </>
  );
};
